<script setup>
import { ref, watch, onUnmounted } from 'vue';
import ContactAPI from 'dashboard/api/contacts';

const props = defineProps({
  modelValue: { type: String, default: '' },
  label: { type: String, required: true },
  placeholder: { type: String, default: '' },
});
const emit = defineEmits(['update:modelValue']);
const chips = ref([]);
const input = ref('');
const suggestions = ref([]);
const focused = ref(false);
let searchTimer;
let searchSequence = 0;
let lastEmitted = '';

const validEmail = value => /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/.test(value);
const syncFromModel = value => {
  const parts = (value || '').split(/[;,\s]+/).filter(Boolean);
  chips.value = parts.filter(validEmail);
  input.value = parts.filter(part => !validEmail(part)).join(' ');
};
watch(
  () => props.modelValue,
  value => {
    if (value !== lastEmitted) syncFromModel(value);
  },
  { immediate: true }
);

const publish = () => {
  lastEmitted = [...chips.value, input.value.trim()].filter(Boolean).join(',');
  emit('update:modelValue', lastEmitted);
};
const add = address => {
  const normalized = address.trim().replace(/^[<\s]+|[>\s]+$/g, '');
  if (!validEmail(normalized)) return false;
  if (
    !chips.value.some(chip => chip.toLowerCase() === normalized.toLowerCase())
  )
    chips.value.push(normalized);
  input.value = '';
  suggestions.value = [];
  publish();
  return true;
};
const commit = () => {
  const parts = input.value.split(/[;,\s]+/).filter(Boolean);
  const invalid = parts.filter(part => !validEmail(part));
  parts.filter(validEmail).forEach(part => {
    if (!chips.value.some(chip => chip.toLowerCase() === part.toLowerCase()))
      chips.value.push(part);
  });
  input.value = invalid.join(' ');
  publish();
  return invalid.length === 0;
};
const onInput = event => {
  input.value = event.target.value;
  if (/[;,\s]/.test(input.value)) commit();
  else publish();
  clearTimeout(searchTimer);
  const query = input.value.trim();
  if (query.length < 2) {
    suggestions.value = [];
    return;
  }
  searchSequence += 1;
  const sequence = searchSequence;
  searchTimer = setTimeout(async () => {
    try {
      const { data } = await ContactAPI.search(query);
      if (sequence === searchSequence)
        suggestions.value = (data.payload || [])
          .filter(contact => validEmail(contact.email || ''))
          .slice(0, 6);
    } catch {
      if (sequence === searchSequence) suggestions.value = [];
    }
  }, 250);
};
const onKeydown = event => {
  if (event.key === 'Enter' || event.key === ',' || event.key === ';') {
    event.preventDefault();
    commit();
  } else if (event.key === 'Tab') {
    commit();
  } else if (event.key === 'Backspace' && !input.value && chips.value.length) {
    chips.value.pop();
    publish();
  }
};
const remove = address => {
  chips.value = chips.value.filter(chip => chip !== address);
  publish();
};
const selectSuggestion = contact => add(contact.email);
onUnmounted(() => clearTimeout(searchTimer));
defineExpose({ commit });
</script>

<template>
  <div class="relative min-w-0">
    <div
      class="flex min-h-10 w-full min-w-0 flex-wrap items-center gap-1.5 rounded-lg border border-[#dadce0] bg-white px-2 py-1 text-sm text-[#202124] focus-within:border-[#16834b]"
    >
      <span class="shrink-0 px-1 text-xs font-medium text-[#5f6368]">{{
        label
      }}</span>
      <span
        v-for="address in chips"
        :key="address"
        class="inline-flex max-w-full items-center gap-1 rounded-full bg-[#e6f4ea] px-2 py-0.5 text-xs text-[#15603e]"
      >
        <span class="truncate">{{ address }}</span>
        <button
          type="button"
          class="shrink-0 rounded-full hover:bg-[#cde8d4]"
          :aria-label="`Remover ${address}`"
          @click="remove(address)"
        >
          <span class="i-lucide-x size-3.5" aria-hidden="true" />
        </button>
      </span>
      <input
        :value="input"
        type="text"
        autocomplete="off"
        class="min-w-24 flex-1 border-0 bg-transparent p-1 text-sm text-[#202124] outline-none placeholder:text-[#80868b]"
        :aria-label="label"
        :placeholder="chips.length ? '' : placeholder"
        @input="onInput"
        @keydown="onKeydown"
        @focus="focused = true"
        @blur="
          focused = false;
          commit();
        "
      />
    </div>
    <div
      v-if="focused && suggestions.length"
      class="absolute inset-x-0 top-full z-20 mt-1 max-h-44 overflow-auto rounded-lg border border-[#dadce0] bg-white py-1 shadow-lg"
    >
      <button
        v-for="contact in suggestions"
        :key="contact.id"
        type="button"
        class="flex w-full flex-col px-3 py-2 text-left hover:bg-[#f1f3f4]"
        @mousedown.prevent="selectSuggestion(contact)"
      >
        <span class="truncate text-xs font-medium text-[#202124]">{{
          contact.name || contact.email
        }}</span>
        <span class="truncate text-xs text-[#5f6368]">{{ contact.email }}</span>
      </button>
    </div>
  </div>
</template>
